import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportingAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Megaphone, RefreshCw, Loader2, Info } from 'lucide-react';

/* Kampagnen-Trichter (Auftrag 26.09.2026)
 *
 * Eine utm_campaign von Bio-Klick / Seitenaufruf über Anfrage, Probetraining
 * und Erscheinen bis zum Vertrag. Zahlen kommen aus /api/reporting/kampagne,
 * Bio-Klicks (Instagram) werden je Woche von Hand eingetragen. Nur mit
 * reports.view sichtbar; ohne das Recht bleibt die Karte weg.
 */

const FARBEN = { bio_klicks: '#EC4899', aufrufe: '#8B5CF6', anfragen: '#3B82F6', gebucht: '#F59E0B', erschienen: '#10B981', abschluesse: '#E60000' };
const ZEITRAEUME = [['7', '7 Tage'], ['30', '30 Tage'], ['90', '90 Tage']];

function quote(v) { return v === null || v === undefined ? '–' : `${Number(v).toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`; }
function isoWoche(d = new Date()) {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const tag = t.getUTCDay() || 7; t.setUTCDate(t.getUTCDate() + 4 - tag);
  const start = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return `${t.getUTCFullYear()}-W${String(Math.ceil(((t - start) / 86400000 + 1) / 7)).padStart(2, '0')}`;
}
function datumVor(tage) { const d = new Date(); d.setDate(d.getDate() - tage + 1); return d.toISOString().slice(0, 10); }

export default function KampagnenTrichterKarte() {
  const navigate = useNavigate();
  const [kampagnen, setKampagnen] = useState(null);      // null = noch nicht geladen / kein Recht
  const [kampagne, setKampagne] = useState('');
  const [tage, setTage] = useState('30');
  const [daten, setDaten] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const [verboten, setVerboten] = useState(false);
  const [bioWoche, setBioWoche] = useState(isoWoche());
  const [bioKlicks, setBioKlicks] = useState('');
  const [bioOffen, setBioOffen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await reportingAPI.kampagneListe();
        setKampagnen(r.data.kampagnen || []);
        setKampagne(r.data.standard || (r.data.kampagnen || [])[0] || '');
      } catch (e) {
        if (e?.response?.status === 403 || e?.response?.status === 401) setVerboten(true);
      }
    })();
  }, []);

  const laden = useCallback(async () => {
    if (!kampagne) return;
    setLaedt(true);
    try {
      const r = await reportingAPI.kampagne({ kampagne, von: datumVor(Number(tage)) });
      setDaten(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kampagnen-Trichter konnte nicht geladen werden');
    } finally { setLaedt(false); }
  }, [kampagne, tage]);
  useEffect(() => { laden(); }, [laden]);

  const bioSpeichern = async () => {
    const n = Number(bioKlicks);
    if (!/^\d{4}-W\d{2}$/.test(bioWoche) || Number.isNaN(n) || n < 0) { toast.error('Woche als 2026-W40 und Klicks als Zahl angeben'); return; }
    try {
      await reportingAPI.bioKlicksSetzen({ kampagne, woche: bioWoche, klicks: Math.round(n) });
      toast.success('Bio-Klicks gespeichert');
      setBioKlicks(''); laden();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Konnte nicht speichern'); }
  };

  if (verboten || kampagnen === null) return null;
  const stufen = daten?.stufen || [];
  const max = Math.max(1, ...stufen.map(s => s.wert || 0));
  const ek = daten?.erster_kontakt;

  return (
    <Card className="border-border/60" data-testid="dashboard-kampagnen">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><Megaphone className="w-4 h-4 text-marke" /> Kampagnen</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <select value={kampagne} onChange={e => setKampagne(e.target.value)} className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="kampagne-auswahl">
              {kampagnen.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
            <select value={tage} onChange={e => setTage(e.target.value)} className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="kampagne-zeitraum">
              {ZEITRAEUME.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={laden} title="Neu laden"><RefreshCw className={`w-3.5 h-3.5 ${laedt ? 'animate-spin' : ''}`} /></Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!daten ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            <div className="text-[11px] text-muted-foreground">{daten.zeitraum.von} bis {daten.zeitraum.bis} · Quote jeweils zur vorherigen Stufe</div>

            {/* Trichter als Balken */}
            <div className="space-y-1.5" data-testid="kampagne-trichter">
              {stufen.map(s => (
                <div key={s.key} className="grid grid-cols-[130px_1fr_auto] sm:grid-cols-[170px_1fr_auto] items-center gap-2 text-xs">
                  <span className="truncate" title={s.label}>{s.label}</span>
                  <div className="h-5 bg-muted rounded overflow-hidden">
                    <div className="h-full rounded" style={{ width: `${Math.max(s.wert ? 3 : 0, (s.wert / max) * 100)}%`, backgroundColor: FARBEN[s.key] || '#999' }} />
                  </div>
                  <span className="tabular-nums text-right w-[92px]">
                    <b>{s.wert.toLocaleString('de-DE')}</b>
                    <span className="text-muted-foreground ml-1">{s.quote === null || s.quote === undefined ? '' : `(${quote(s.quote)})`}</span>
                  </span>
                </div>
              ))}
            </div>

            {daten.trichter.anfragen === 0 && daten.trichter.aufrufe === 0 && (
              <p className="text-xs text-muted-foreground">Noch keine Aufrufe oder Anfragen mit dieser Kampagne im Zeitraum.</p>
            )}

            {/* Rueckruf-Kontrolle */}
            {ek && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs rounded-md border border-border/60 bg-muted/30 px-3 py-2" data-testid="kampagne-kontakt">
                <span><b>Anfrage → erster Kontakt:</b> {ek.median_stunden === null ? 'noch kein Kontakt erfasst' : `Median ${ek.median_stunden.toLocaleString('de-DE')} h`}</span>
                <span>innerhalb 24 h: <b>{ek.innerhalb_24h}</b> von {daten.trichter.anfragen} ({quote(ek.quote_24h)})</span>
                {ek.offen > 0 && <span className="text-amber-600">{ek.offen} ohne erfassten Kontakt</span>}
              </div>
            )}

            {/* Segmente + Quellen */}
            {(daten.segmente.length > 0 || daten.quellen.length > 0) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {daten.segmente.length > 0 && (
                  <table className="w-full text-xs" data-testid="kampagne-segmente">
                    <thead className="text-muted-foreground"><tr><th className="text-left py-1 font-medium">Segment</th><th className="text-right py-1 font-medium">Anfr.</th><th className="text-right py-1 font-medium">Geb.</th><th className="text-right py-1 font-medium">Ersch.</th><th className="text-right py-1 font-medium">Vertr.</th><th className="text-right py-1 font-medium">Quote</th></tr></thead>
                    <tbody>{daten.segmente.map(z => (
                      <tr key={z.segment} className="border-t border-border/40"><td className="py-1">{z.label}</td><td className="py-1 text-right tabular-nums">{z.anfragen}</td><td className="py-1 text-right tabular-nums">{z.gebucht}</td><td className="py-1 text-right tabular-nums">{z.erschienen}</td><td className="py-1 text-right tabular-nums font-bold">{z.abschluesse}</td><td className="py-1 text-right tabular-nums text-muted-foreground">{quote(z.quote_abschluss)}</td></tr>
                    ))}</tbody>
                  </table>
                )}
                {daten.quellen.length > 0 && (
                  <table className="w-full text-xs" data-testid="kampagne-quellen">
                    <thead className="text-muted-foreground"><tr><th className="text-left py-1 font-medium">utm_source</th><th className="text-right py-1 font-medium">Aufr.</th><th className="text-right py-1 font-medium">Anfr.</th><th className="text-right py-1 font-medium">Geb.</th><th className="text-right py-1 font-medium">Ersch.</th><th className="text-right py-1 font-medium">Vertr.</th></tr></thead>
                    <tbody>{daten.quellen.map(z => (
                      <tr key={z.quelle} className="border-t border-border/40 hover:bg-muted/40 cursor-pointer" title="Leads dieser Kampagne und Quelle anzeigen"
                          onClick={() => navigate(`/leads?kampagne=${encodeURIComponent(daten.kampagne)}&quelle=${encodeURIComponent(z.quelle)}`)}>
                        <td className="py-1 font-medium">{z.quelle}</td><td className="py-1 text-right tabular-nums">{z.aufrufe}</td><td className="py-1 text-right tabular-nums">{z.anfragen}</td><td className="py-1 text-right tabular-nums">{z.gebucht}</td><td className="py-1 text-right tabular-nums">{z.erschienen}</td><td className="py-1 text-right tabular-nums font-bold">{z.abschluesse}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}
              </div>
            )}

            {/* Bio-Klicks von Hand */}
            <div className="text-xs">
              <button type="button" className="text-primary hover:underline" onClick={() => setBioOffen(!bioOffen)} data-testid="bio-klicks-toggle">
                Bio-Klicks (Instagram) eintragen {daten.bio_klicks.summe ? `· ${daten.bio_klicks.summe} im Zeitraum` : ''}
              </button>
              {bioOffen && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Input value={bioWoche} onChange={e => setBioWoche(e.target.value)} placeholder="2026-W40" className="h-8 w-28 text-xs" data-testid="bio-woche" />
                  <Input value={bioKlicks} onChange={e => setBioKlicks(e.target.value)} placeholder="Klicks" inputMode="numeric" className="h-8 w-24 text-xs" data-testid="bio-klicks" />
                  <Button size="sm" className="h-8" onClick={bioSpeichern}>Speichern</Button>
                  <span className="text-muted-foreground flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Zahl aus den Instagram-Insights je Woche; sie wird die oberste Stufe.</span>
                </div>
              )}
              {bioOffen && daten.bio_klicks.eintraege.length > 0 && (
                <div className="mt-1 text-muted-foreground">{daten.bio_klicks.eintraege.map(e => `${e.woche}: ${e.klicks}`).join(' · ')}</div>
              )}
            </div>

            <button type="button" className="text-xs text-primary hover:underline" onClick={() => navigate(`/leads?kampagne=${encodeURIComponent(daten.kampagne)}`)} data-testid="kampagne-leads-link">
              Alle Leads dieser Kampagne anzeigen →
            </button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
